import React, { useEffect, useState, useMemo, useRef } from "react";
import getSubfragm from "../../../../graphics/get-subfragm";
import { prepareSplitHOC } from "../../../../hoc/prepareSplitHOC";
import { throttle2 } from "../../../../utils/throttle";
import { adjustSignalStart } from "../../../../utils/adjustSignalStart";

import MeasurerGraphMain from "./measurer-graph-main/measurer-graph-main";
import Slider from "../../../ui/SliderComponent/SliderComponent";
import MeasurerGraphNav from "./measurer-graph-nav/measurer-graph-nav";
import { ReactComponent as SvgMagnify } from "../../../../assets/magnify.svg";
import { ReactComponent as SvgCollapseV } from "../../../../assets/collapse-vertical.svg";
import { ReactComponent as SvgCollapseH } from "../../../../assets/collapse-horizontal.svg";
import { ReactComponent as SvgExpandV } from "../../../../assets/expand-vertical.svg";
import { ReactComponent as SvgExpandH } from "../../../../assets/expand-horizontal.svg";
import { ErrorIndicator, Spinner } from "../../../state-indicators";

import { SizeController } from "../../../../utils/sizeController";

const calcSignalRhythmStart = (_Start, _WindowLength, _RhStart, _RhWindowLength) => {
    const step = (_RhWindowLength - _WindowLength) / 2;
    const edge = Math.round(_Start - (_Start % (_RhWindowLength - _WindowLength)));
    _RhStart = _RhStart ?? 0;
    let ans = _RhStart;
    if (_RhStart < edge) ans = Math.round(_Start - (_Start % step) - step); // если целую перемотку делать то убрать - step
    else if (_RhStart > edge) ans = Math.round(_Start - (_Start % step));

    return ans;
};

const MeasurerGraph = props => {
    const { signalLength, onDragMove, areaWidth, areaForceUpdate, onAreaWidthChange, loc, scale, xScales, yScales } = props;
    const peaks = useRef([]);
    const [state, setState] = useState("loading"); //"idle, error, loading"
    const [signalData, setSignalData] = useState([]);
    const [rhythmData, setRhythmData] = useState([]);
    const [signalStart, setSignalStart] = useState(0);
    const [navGraphWidth, setNavGraphWidth] = useState(null);
    const [mainGraphWidth, setMainGraphWidth] = useState(null);
    const [refreshFlag, setRefreshFlag] = useState(false);
    const [signalRhythmStart, setSignalRhythmStart] = useState(0);
    const mainGraphRef = useRef(null);
    const hasStartedRef = useRef(false);

    useEffect(() => {
        const sizeController = new SizeController({
            observable: ".resizer-container",
            changeable: ".popup-window",
            totalSectorsWidth: 38, // columnSpace - 38
            totalSectorsHeight: 10,
            chartCount: 2,
            truncWidthSquares: 1,
            truncHeightSquares: 1,
            mainGraphHeightPercentage: 0.85,
            rhythmGraphHeightPercentage: 0.15
        });
        sizeController.observe();
        const mount = async () => {
            prepareSignalService();
            prepareSignalBounds();
        };

        mount();

        return () => {
            sizeController.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hasStartedRef.current && mainGraphWidth > 0) {
            setStart();
            hasStartedRef.current = true;
        }
        if (signalStart !== null) {
            const { channel } = props.rhythmScale;

            // adjust signalStart so it wouldn't go out of bounds of signalLength
            const adjustedSignalStart = adjustSignalStart(signalStart, mainGraphWidth, signalLength);
            updateSignalData(adjustedSignalStart, Math.min(mainGraphWidth, signalLength), {
                start: signalRhythmStart,
                length: Math.min(navGraphWidth, signalLength - signalRhythmStart),
                channel
            });
            updatePeaks();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalStart, mainGraphWidth]);

    const prepareSignalService = () => {
        const { signalService } = props;
        signalService.dataPoster.onAnswer = onSignalServiceAnswer;
        signalService.dataPoster.onError = onSignalServiceError;
    };

    const setStart = async () => {
        const { Q, S } = props;
        const { start } = getSubfragm(signalLength, Q + S / 2, mainGraphWidth);
        setSignalStart(start);
    };

    const prepareSignalBounds = () => {
        if (!props.prepared) {
            return;
        }

        const _signalRhythmStart = calcSignalRhythmStart(signalStart, mainGraphWidth, signalRhythmStart, navGraphWidth);

        setSignalRhythmStart(_signalRhythmStart);
    };

    const updatePeaks = () => {
        const { complexes } = props;
        let inds = [];
        const { Qs: _qs, Ws: _ws } = complexes;
        for (let i = 0; i < _qs.length; i++) {
            if (_qs[i] < signalStart || _qs[i] > signalStart + mainGraphWidth) {
                continue;
            }
            inds.push(i);
        }
        let Qs = inds.map(i => _qs[i] - signalStart);
        let Ss = inds.map(i => _ws[i] + _qs[i] - signalStart);

        peaks.current = [Qs, Ss];
    };

    const onSignalServiceAnswer = data => {
        const { lead_signals, rhythm_signal } = data;
        setState("idle");
        setSignalData(lead_signals);
        setRhythmData(rhythm_signal);
    };

    const onSignalServiceError = error => {
        console.log(error);
        setState("error");
    };

    const updateSignalData = (start, length, extraSignal) => {
        props.reqSignalThrottling(start, length, extraSignal);
    };

    const onSliderChange = useMemo(() => {
        return throttle2(value => {
            setSignalStart(value);
            setSignalRhythmStart(Math.floor(value / (navGraphWidth - mainGraphWidth)) * (navGraphWidth - mainGraphWidth));
        }, 100);
    }, [navGraphWidth, mainGraphWidth]);

    const changeScaleX = type => {
        const { onChange } = props;

        const [index, graphWidth] = mainGraphRef.current.changeScaleX(type);

        const adjustedSignalStart = adjustSignalStart(signalStart, graphWidth, signalLength);
        setSignalStart(adjustedSignalStart);
        const adjustedSignalRhtyhmStart = Math.floor(signalStart / (navGraphWidth - graphWidth)) * (navGraphWidth - graphWidth);
        setSignalRhythmStart(adjustedSignalRhtyhmStart);

        onChange({
            xScaleIndex: index
        });
    };

    const changeScaleY = async type => {
        const { onChange } = props;

        const index = await mainGraphRef.current.changeScaleY(type);
        onChange({
            yScaleIndex: index
        });
    };

    const sliderMax = Math.max(0, signalLength - mainGraphWidth);
    return (
        <>
            <div className="whole-graph__bar">
                <Slider value={signalStart} max={sliderMax} onChange={onSliderChange} />

                <div className="whole-graph__magnifier">
                    <div className="icon">
                        <SvgMagnify />
                    </div>
                    <div className="divider" />
                    <button
                        type="button"
                        disabled={scale.yScaleIndex === 0}
                        onClick={() => changeScaleY("decrease")}
                        className="button toolbar__button">
                        <SvgCollapseV />
                    </button>
                    <button
                        type="button"
                        disabled={scale.yScaleIndex === yScales.length - 1}
                        onClick={() => changeScaleY("increase")}
                        className="button toolbar__button">
                        <SvgExpandV />
                    </button>
                    <button
                        type="button"
                        disabled={scale.xScaleIndex === 0}
                        onClick={() => changeScaleX("decrease")}
                        className="button toolbar__button">
                        <SvgCollapseH />
                    </button>
                    <button
                        type="button"
                        disabled={scale.xScaleIndex === xScales.length - 1}
                        onClick={() => changeScaleX("increase")}
                        className="button toolbar__button">
                        <SvgExpandH />
                    </button>
                </div>
            </div>
            <div className="graphContainer">
                <div className={"wholeGraph" + (state === "error" ? " error" : "")}>
                    <div className={"wholeGraph__graphWrap" + (state === "error" ? " error" : "")}>
                        <MeasurerGraphMain
                            Q={props.Q}
                            S={props.S}
                            ref={mainGraphRef}
                            refreshFlag={refreshFlag}
                            peaks={peaks}
                            signalData={signalData}
                            signalStart={signalStart}
                            signalLength={signalLength}
                            mainGraphWidth={mainGraphWidth}
                            areaForceUpdate={areaForceUpdate}
                            areaWidth={areaWidth}
                            onAreaWidthChange={onAreaWidthChange}
                            onDragMove={onDragMove}
                            userSweepUnits={loc("units.mm_sec")}
                            setMainGraphWidth={setMainGraphWidth}
                            setRefreshFlag={setRefreshFlag}
                            scale={scale}
                        />
                        {state === "error" ? (
                            <ErrorIndicator withCaption small {...{ errorCaptionKey: "errors.dataUnavailable" }} />
                        ) : state === "loading" ? (
                            <Spinner small transparent />
                        ) : null}
                    </div>
                </div>
                <div className={"wholeGraphRhythm" + (state === "error" ? " error" : "")}>
                    <div className={"wholeGraphRhythm__graphWrap" + (state === "error" ? " error" : "")}>
                        <MeasurerGraphNav
                            Q={props.Q}
                            S={props.S}
                            refreshFlag={refreshFlag}
                            signalData={rhythmData}
                            signalStart={signalStart}
                            signalLength={signalLength}
                            mainGraphWidth={mainGraphWidth}
                            signalRhythmStart={signalRhythmStart}
                            userSweepUnits={loc("units.mm_sec")}
                            setNavGraphWidth={setNavGraphWidth}
                            setSignalRhythmStart={setSignalRhythmStart}
                        />
                        {state === "error" ? (
                            <ErrorIndicator withCaption small {...{ errorCaptionKey: "errors.dataUnavailable" }} />
                        ) : state === "loading" ? (
                            <Spinner small transparent />
                        ) : null}
                    </div>
                </div>
            </div>
        </>
    );
};

export default prepareSplitHOC(MeasurerGraph);
